/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaBuilding, FaSearch, FaArrowLeft, FaEye, FaTimes, FaClock, FaCheckCircle, FaTimesCircle, FaTrash } from 'react-icons/fa';
import '../pages_premium.css';
import './PropertyRequests.css';
import AdminPagination from '../components/common/AdminPagination';

export default function PropertyIndividualRequests() {
    const { propertyId } = useParams();
    const navigate = useNavigate();

    const [property, setProperty] = useState(null);
    const [requests, setRequests] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/admin/property-requests/${propertyId}`);
            setProperty(res.data.property || null);
            setRequests(res.data.requests || []);
        } catch (error) {
            console.error('Error fetching property requests:', error);
            toast.error('Failed to load property requests');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRequest = async (id) => {
        if (!window.confirm('Are you sure you want to delete this property request? This will permanently remove the request and any linked messages/inspection.')) {
            return;
        }

        try {
            await axiosInstance.delete(`/admin/property-requests/${id}`);
            toast.success('Property request deleted successfully');
            if (selectedRequest && selectedRequest.id === id) {
                setSelectedRequest(null);
            }
            fetchRequests();
        } catch (error) {
            console.error('Error deleting property request:', error);
            toast.error(error.response?.data?.message || 'Failed to delete property request');
        }
    };

    useEffect(() => {
        if (propertyId) {
            fetchRequests();
        }
    }, [propertyId]);

    const formatDate = (val) => {
        if (!val) return '-';
        const dateObj = new Date(val);
        if (!isNaN(dateObj.getTime())) {
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();
            return `${day}/${month}/${year}`;
        }
        return String(val);
    };

    const getRoleName = (role) => {
        if (!role) return 'Student';
        const r = String(role).toLowerCase();
        if (r === 'student') return 'Student';
        if (r === 'owner') return 'Business Owner';
        if (r === 'agent' || r === 'lister') return 'Lister';
        if (r === 'hiremate' || r === 'worker') return 'Worker';
        return r.charAt(0).toUpperCase() + r.slice(1);
    };

    const getRoleBadgeClass = (role) => {
        const r = String(role || '').toLowerCase();
        if (r === 'student') return 'role-badge role-badge-student';
        if (r === 'lister' || r === 'agent') return 'role-badge role-badge-lister';
        if (r === 'business owner' || r === 'owner' || r === 'hiremate' || r === 'worker') return 'role-badge role-badge-owner';
        return 'role-badge role-badge-default';
    };

    const getStatusBadge = (status) => {
        const s = String(status || 'pending').toLowerCase();
        if (s === 'accepted') {
            return (
                <span className="status-badge status-badge-accepted">
                    <FaCheckCircle size={11} /> Accepted
                </span>
            );
        }
        if (s === 'rejected') {
            return (
                <span className="status-badge status-badge-rejected">
                    <FaTimesCircle size={11} /> Rejected
                </span>
            );
        }
        return (
            <span className="status-badge status-badge-pending">
                <FaClock size={11} /> Pending
            </span>
        );
    };

    const filtered = requests.filter(r =>
        r.requested_by?.toLowerCase().includes(search.toLowerCase()) ||
        r.request_type?.toLowerCase().includes(search.toLowerCase()) ||
        r.requester_type?.toLowerCase().includes(search.toLowerCase()) ||
        r.email?.toLowerCase().includes(search.toLowerCase()) ||
        r.phone?.toLowerCase().includes(search.toLowerCase()) ||
        r.status?.toLowerCase().includes(search.toLowerCase())
    );

    const paginatedRequests = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <div className="page-premium-wrapper properties-list-page">
            <div className="page-premium-content">

                {/* Property Header Identification Banner */}
                <div className="prop-header-banner">
                    <div className="prop-header-title-wrap">
                        <button
                            type="button"
                            className="prop-header-back-btn"
                            onClick={() => navigate('/property-requests')}
                        >
                            <FaArrowLeft size={12} /> Back to Properties
                        </button>
                        <div>
                            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                Selected Property
                            </div>
                            <h2 className="prop-header-title-text m-0">
                                Property: {property?.title || 'Loading...'}
                            </h2>
                        </div>
                    </div>

                    {property && (
                        <div className="prop-header-meta">
                            <div className="prop-meta-item">
                                <span>Listed by:</span>
                                <strong>{property.listed_by}</strong>
                            </div>
                            <div className="prop-meta-item">
                                <span>Role:</span>
                                <span className={getRoleBadgeClass(property.listed_type)}>
                                    {property.listed_type}
                                </span>
                            </div>
                            <div className="prop-meta-item">
                                <span>Total Requests:</span>
                                <span className="req-count-badge">{requests.length} Requests</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            All Requests for Property
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by requested by, email, or status..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '50px' }}>#</th>
                                    <th style={{ minWidth: '180px' }}>Property</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Requested By</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Request Type</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Listed By</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Request Date</th>
                                    <th style={{ textAlign: 'center' }}>Status</th>
                                    <th style={{ textAlign: 'center' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="10" style={{ textAlign: 'center', padding: '40px 0' }}>
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>Loading requests for this property...</p>
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="10">
                                            <div className="ptable-empty">
                                                <div className="ptable-empty-icon">📭</div>
                                                <p>No requests found for this property.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedRequests.map((reqItem, idx) => (
                                        <tr key={reqItem.id}>
                                            <td style={{ color: '#9ba6c0', fontWeight: 600 }}>#{(currentPage - 1) * pageSize + idx + 1}</td>
                                            <td style={{ minWidth: '180px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <FaBuilding className="text-muted" size={14} />
                                                    <strong className="prop-title-text" style={{ fontSize: '0.88rem' }}>
                                                        {reqItem.property_title}
                                                    </strong>
                                                </div>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                                                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '11px', color: '#334155' }}>
                                                        {reqItem.requested_by ? reqItem.requested_by.charAt(0).toUpperCase() : 'U'}
                                                    </div>
                                                    <span style={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{reqItem.requested_by}</span>
                                                </div>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <span className={getRoleBadgeClass(reqItem.request_type || reqItem.requester_type || reqItem.requested_by_user?.role)}>
                                                    {reqItem.request_type || reqItem.requester_type || getRoleName(reqItem.requested_by_user?.role)}
                                                </span>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <span style={{ fontWeight: 500, color: '#334155', whiteSpace: 'nowrap' }}>{reqItem.listed_by}</span>
                                            </td>
                                            <td>{reqItem.email || '-'}</td>
                                            <td>{reqItem.phone || '-'}</td>
                                            <td>{formatDate(reqItem.request_date)}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                {getStatusBadge(reqItem.status)}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                                                    <button
                                                        type="button"
                                                        className="action-btn action-btn-view"
                                                        data-tooltip="View Details"
                                                        onClick={() => setSelectedRequest(reqItem)}
                                                    >
                                                        <FaEye />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="action-btn action-btn-delete"
                                                        data-tooltip="Delete Request"
                                                        onClick={() => handleDeleteRequest(reqItem.id)}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <AdminPagination
                        currentPage={currentPage}
                        totalItems={filtered.length}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                        itemLabel="requests"
                    />
                </div>

            </div>

            {/* Request Details Modal */}
            {selectedRequest && (
                <div className="modal-overlay" onClick={() => setSelectedRequest(null)}>
                    <div className="modal-dialog-custom" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-custom">
                            <h3>Property Request Details</h3>
                            <button
                                type="button"
                                className="modal-close-btn"
                                onClick={() => setSelectedRequest(null)}
                            >
                                <FaTimes size={18} />
                            </button>
                        </div>
                        <div className="modal-body-custom">
                            <div className="modal-field">
                                <div className="modal-label">Property</div>
                                <div className="modal-val" style={{ fontWeight: 700 }}>
                                    {selectedRequest.property_title}
                                </div>
                            </div>

                            <div className="admin-2col-grid" style={{ gap: '14px', marginBottom: '14px' }}>
                                <div>
                                    <div className="modal-label">Requested By</div>
                                    <div className="modal-val font-weight-bold">{selectedRequest.requested_by}</div>
                                </div>
                                <div>
                                    <div className="modal-label">Request Type</div>
                                    <div className="modal-val">
                                        <span className={getRoleBadgeClass(selectedRequest.request_type || selectedRequest.requester_type || selectedRequest.requested_by_user?.role)}>
                                            {selectedRequest.request_type || selectedRequest.requester_type || getRoleName(selectedRequest.requested_by_user?.role)}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <div className="modal-label">Request Status</div>
                                    <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                                </div>
                                <div>
                                    <div className="modal-label">Applicant Email</div>
                                    <div className="modal-val">{selectedRequest.email || '-'}</div>
                                </div>
                                <div>
                                    <div className="modal-label">Applicant Phone</div>
                                    <div className="modal-val">{selectedRequest.phone || '-'}</div>
                                </div>
                                <div>
                                    <div className="modal-label">Listed By</div>
                                    <div className="modal-val">{selectedRequest.listed_by}</div>
                                </div>
                                <div>
                                    <div className="modal-label">Lister Role</div>
                                    <div className="modal-val">
                                        <span className={getRoleBadgeClass(selectedRequest.listed_type)}>
                                            {selectedRequest.listed_type}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <div className="modal-label">Request Date</div>
                                    <div className="modal-val">{formatDate(selectedRequest.request_date)}</div>
                                </div>
                                <div>
                                    <div className="modal-label">Conversation ID</div>
                                    <div className="modal-val">#{selectedRequest.id}</div>
                                </div>
                            </div>

                            {selectedRequest.inspection && (
                                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', marginBottom: '14px' }}>
                                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', marginBottom: '4px' }}>
                                        Linked Confirmed Inspection
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: '#1e3a8a' }}>
                                        📅 <strong>Date:</strong> {selectedRequest.inspection.inspection_date} &nbsp;|&nbsp; ⏰ <strong>Time:</strong> {selectedRequest.inspection.inspection_time || '10:00 AM'} &nbsp;|&nbsp; <strong>Status:</strong> {selectedRequest.inspection.status}
                                    </div>
                                </div>
                            )}

                            {selectedRequest.last_message && (
                                <div className="modal-field">
                                    <div className="modal-label">Latest Message / Enquiry Note</div>
                                    <div className="modal-val-box">
                                        {selectedRequest.last_message}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-custom" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                onClick={() => handleDeleteRequest(selectedRequest.id)}
                            >
                                <FaTrash size={11} /> Delete Request
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => setSelectedRequest(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
